import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import supabase from '../lib/supabase'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth()
  const [isDarkMode, setIsDarkMode] = useState(true) // Default to current mystical theme

  useEffect(() => {
    // Load theme preference
    const loadTheme = async () => {
      if (user) {
        // Load from user profile in Supabase
        try {
          const { data: profile, error } = await supabase
            .from('profiles_tp2024')
            .select('theme_preference')
            .eq('id', user.id)
            .single()

          if (!error && profile?.theme_preference) {
            setIsDarkMode(profile.theme_preference === 'dark')
          }
        } catch (error) {
          console.error('Error loading theme preference:', error)
        }
      } else {
        // Load from localStorage for non-authenticated users
        const savedTheme = localStorage.getItem('tarotPairs_theme')
        if (savedTheme) {
          setIsDarkMode(savedTheme === 'dark')
        }
      }
    }

    loadTheme()
  }, [user])

  useEffect(() => {
    // Apply theme to document
    if (isDarkMode) {
      document.documentElement.classList.add('dark-theme')
      document.documentElement.classList.remove('light-theme')
    } else {
      document.documentElement.classList.add('light-theme')
      document.documentElement.classList.remove('dark-theme')
    }

    // Save theme preference
    const saveTheme = async () => {
      const themeValue = isDarkMode ? 'dark' : 'light'
      
      if (user) {
        // Save to Supabase user profile
        try {
          await supabase
            .from('profiles_tp2024')
            .update({ theme_preference: themeValue })
            .eq('id', user.id)
        } catch (error) {
          console.error('Error saving theme preference:', error)
          // Fallback to localStorage
          localStorage.setItem('tarotPairs_theme', themeValue)
        }
      } else {
        // Save to localStorage for non-authenticated users
        localStorage.setItem('tarotPairs_theme', themeValue)
      }
    }

    saveTheme()
  }, [isDarkMode, user])

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev)
  }

  const value = {
    isDarkMode,
    toggleTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}